import { isEmpty, isUndefined, noop } from 'lodash';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/Button';
import { DayPicker } from 'components/DayPicker/DayPicker';
import { FlashMessage } from 'components/FlashMessages';
import { SelectInput, TextAreaInput } from 'components/Forms';
import { Loader } from 'components/Loader';
import { TimeSlotPicker } from 'components/TimeSlotPicker/TimeSlotPicker';
import { Heading, Paragraph } from 'components/Typography';
import { Appointment } from 'domains/appointments';
import { RescheduleReason, getRescheduleReasonOptions } from 'domains/appointments/reschedule/reasons';
import { AvailableTimes } from 'domains/calendars/types';
import { getFirstAvailableDayIndex } from 'domains/consultations/freeConsultationSchedule';
import { useTherapistSupportNumber } from 'domains/support/therapistsNumber';
import { useDisplayTimezone } from 'domains/timezone/useDisplayTimezone';
import { formatPhoneNumber } from 'utils/phoneNumber';
import { ButtonLink } from 'components/ButtonLink';
import { routes } from 'router/routes';
import { Link } from 'components/Link';
import { useTrack } from 'utils/hooks/useMixPanel';
import { APPOINTMENTS_MIXPANEL_EVENTS } from 'domains/appointments/mixpanel';
import { RescheduleAppointmentFormData } from 'pages/RescheduleAppointment';
import { getMaxRescheduleDate } from 'domains/appointments/reschedule/calendar';
import { AppointmentCancellationCard } from 'components/AppointmentCancellationCard';

type Props = {
  onSubmit: (data: RescheduleAppointmentFormData) => void;
  availableTimes: AvailableTimes;
  isFetchingNextPage: boolean;
  onFetchNextPage: () => void;
  appointment: Appointment;
};

const PickRescheduleDate: React.FC<Props> = ({
  onSubmit,
  availableTimes,
  isFetchingNextPage,
  onFetchNextPage,
  appointment,
}) => {
  const { track } = useTrack(APPOINTMENTS_MIXPANEL_EVENTS.selfReschedule.visited);
  const { displayTimezone } = useDisplayTimezone();
  if (!displayTimezone) {
    throw new Error(`Expected displayTimezone to be set`);
  }
  const [rescheduleReason, setRescheduleReason] = useState<string>();
  const [rescheduleReasonOther, setRescheduleReasonOther] = useState<string>();

  const [selectedDayIndex, setSelectedDayIndex] = useState<number | undefined>(
    getFirstAvailableDayIndex(availableTimes),
  );
  const [timeSlot, setTimeSlot] = useState<Date | undefined>();

  const currentSlots = isUndefined(selectedDayIndex) ? undefined : availableTimes[selectedDayIndex].availableTimes;

  const onChangeDay = useCallback((newDayIndex: number) => {
    setSelectedDayIndex(newDayIndex);
    setTimeSlot(undefined);
  }, []);

  const { t } = useTranslation();

  useEffect(() => {
    if (timeSlot) {
      track(APPOINTMENTS_MIXPANEL_EVENTS.selfReschedule.pickedDate);
    }
  }, [timeSlot, track]);

  const isFormComplete = useMemo(() => {
    if (isUndefined(timeSlot)) return false;

    if (isUndefined(rescheduleReason)) return false;

    if (rescheduleReason === RescheduleReason.other) {
      return !isEmpty(rescheduleReasonOther);
    }

    return true;
  }, [timeSlot, rescheduleReason, rescheduleReasonOther]);

  return (
    <>
      <Heading level="h2" className="mb-16 text-left">
        {t(`appointment.reschedule.mainHeading`)}
      </Heading>
      <Heading level="h7" useDefaultMargins>
        {t(`appointment.reschedule.subtitle`, {
          timezone: displayTimezone?.label,
        })}
      </Heading>

      <AppointmentCancellationCard appointment={appointment} className="mb-40" />

      <FlashMessage className="mb-64 sm-d:mb-40" variant="info">
        {t(`appointment.reschedule.warning`)}
      </FlashMessage>

      <DayPicker
        selectedDayIndex={selectedDayIndex}
        onChange={onChangeDay}
        availableTimes={availableTimes}
        isScrollLoading={isFetchingNextPage}
        onInfiniteScroll={onFetchNextPage}
        maxDay={getMaxRescheduleDate(appointment.appointmentDateTime)}
      />

      {currentSlots?.length && <TimeSlotPicker onChange={setTimeSlot} timeSlots={currentSlots} value={timeSlot} />}

      <SelectInput
        label={t('appointment.reschedule.rescheduleReason.title')}
        options={getRescheduleReasonOptions(t)}
        name="rescheduleReason"
        value={rescheduleReason}
        onChange={(e: ChangeEvent<HTMLSelectElement>) => {
          const newValue = e.target.value;
          setRescheduleReason(isEmpty(newValue) ? undefined : newValue);
          setRescheduleReasonOther('');
        }}
        clearValue={noop}
        setError={noop}
        setValue={noop}
        disabled={false}
        className="mb-64 sm-d:mb-40"
        allowEmpty
      />

      {rescheduleReason === RescheduleReason.other && (
        <TextAreaInput
          className="mb-64 sm-d:mb-40"
          clearValue={noop}
          setError={noop}
          setValue={noop}
          disabled={false}
          isControlled
          rows={8}
          name="rescheduleReasonOther"
          label={t('appointment.reschedule.rescheduleReasonOther.title')}
          // @TODO(juan): issue with missing textarea event types, .value should always be present
          onChange={e => setRescheduleReasonOther((e as unknown as ChangeEvent<HTMLInputElement>).target.value)}
        />
      )}

      <PhoneNumberSection />

      <div className="flex sm-d:flex-col gap-16">
        <Button
          className="sm-d:w-full"
          size="lg"
          disabled={!isFormComplete}
          onClick={() =>
            isFormComplete &&
            onSubmit({
              displayTimezone: displayTimezone.value,
              newDate: timeSlot,
              rescheduledReason: rescheduleReason,
              rescheduledReasonOther: rescheduleReasonOther,
            } as RescheduleAppointmentFormData)
          }
        >
          {t('appointment.reschedule.continue.button')}
        </Button>

        <ButtonLink className="sm-d:w-full" size="lg" variant="ghost" to={routes.root}>
          {t('appointment.reschedule.keepOriginal')}
        </ButtonLink>
      </div>
    </>
  );
};

export const PhoneNumberSection = () => {
  const { data, isSuccess, isLoading } = useTherapistSupportNumber();

  const { t } = useTranslation();

  if (isLoading) {
    return <Loader defaultStyles />;
  }
  if (!isSuccess) {
    return null;
  }
  return (
    <Paragraph className="mb-64 sm-d:mb-40">
      {t('appointment.reschedule.therapistPhoneNumber')}{' '}
      <Link to={`tel:${data.phoneNumber}`} disableExternalLinkIcon includeDefaultClasses>
        {formatPhoneNumber(data.phoneNumber)}.
      </Link>
    </Paragraph>
  );
};

export default PickRescheduleDate;
